import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LANDING } from "@/constants/testIds";
import { openCookiePreferences } from "@/components/CookieConsent";

export default function Footer() {
  const { t } = useTranslation();

  const COLS = [
    {
      title: t("footer.colProject"),
      links: [
        { label: t("nav.about"), to: "/about", testId: "footer-link-about" },
        { label: t("nav.activities"), to: "/activities", testId: "footer-link-activities" },
        { label: t("nav.treasures2026"), to: "/treasures-2026", testId: "footer-link-treasures" },
      ],
    },
    {
      title: t("footer.colMagazine"),
      links: [
        { label: t("nav.euToday"), to: "/eu-today", testId: "footer-link-eu-today" },
        { label: t("nav.eu27"), to: "/country/italy", testId: "footer-link-eu-27" },
        { label: t("footer.photoContest"), to: "/articles/in-between-2026-05-04", testId: "footer-link-photo-contest" },
      ],
    },
    {
      title: t("footer.colLegal"),
      links: [
        { label: t("footer.privacy"), to: "/privacy", testId: "footer-link-privacy" },
        { label: t("footer.cookies"), to: "/cookies", testId: "footer-link-cookies" },
        { label: t("nav.contact"), to: "/contact", testId: "footer-link-contact" },
      ],
    },
  ];

  return (
    <footer
      id="contatti"
      data-testid={LANDING.footer}
      className="bg-[#0f2440] text-white pt-20 md:pt-28 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <Link to="/" className="flex items-baseline gap-2 group">
              <span className="font-display text-2xl md:text-3xl tracking-wide text-white">
                TESORI
              </span>
              <span className="font-display text-2xl md:text-3xl tracking-wide text-[#d4af37]">
                D&apos;EUROPA
              </span>
            </Link>
            <span className="block mt-2 text-[10px] tracking-[0.4em] uppercase text-white/55">
              {t("footer.tagline")}
            </span>
            <p className="font-body text-white/70 text-sm md:text-[15px] leading-[1.85] mt-6 max-w-md">
              {t("footer.description")}
            </p>

            <div className="mt-8 flex items-center gap-3">
              <a
                href="https://www.facebook.com/61584106230976/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={LANDING.footerSocialFacebook}
                aria-label="Facebook"
                className="h-10 w-10 inline-flex items-center justify-center border border-white/25 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
              >
                <Facebook size={16} strokeWidth={1.6} />
              </a>
              <a
                href="https://www.linkedin.com/company/tesori-d-europa/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={LANDING.footerSocialPinterest}
                aria-label="LinkedIn"
                className="h-10 w-10 inline-flex items-center justify-center border border-white/25 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
              >
                <Linkedin size={16} strokeWidth={1.6} />
              </a>
              <a
                href="https://www.instagram.com/tesorideuropa/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={LANDING.footerSocialInstagram}
                aria-label="Instagram"
                className="h-10 w-10 inline-flex items-center justify-center border border-white/25 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
              >
                <Instagram size={16} strokeWidth={1.6} />
              </a>
            </div>

            <a
              href="mailto:info@tesorideuropa.com"
              data-testid={LANDING.footerEmail}
              className="link-underline inline-flex items-center gap-2 mt-10 text-white/85 font-body"
            >
              info@tesorideuropa.com
              <ArrowUpRight size={14} strokeWidth={1.8} />
            </a>
          </div>

          {/* Quick links */}
          <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-10">
            {COLS.map((col) => (
              <div key={col.title}>
                <h4 className="text-[11px] tracking-[0.32em] uppercase text-[#d4af37] font-medium">
                  {col.title}
                </h4>
                <ul className="mt-5 space-y-3">
                  {col.links.map((l) => (
                    <li key={l.testId}>
                      <Link
                        to={l.to}
                        data-testid={l.testId}
                        className="font-body text-white/80 hover:text-[#d4af37] text-[14px] transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-[11px] tracking-[0.28em] uppercase text-white/60 font-body">
            © {new Date().getFullYear()} Tesori d&apos;Europa · {t("footer.copyright")}
          </p>
          <div className="flex items-center gap-6">
            <button
              data-testid="footer-cookie-preferences-btn"
              onClick={openCookiePreferences}
              className="text-[11px] tracking-[0.28em] uppercase text-white/60 hover:text-[#d4af37] font-body transition-colors"
            >
              {t("footer.cookiePreferences") || "Cookie Preferences"}
            </button>
            <p className="text-[11px] tracking-[0.28em] uppercase text-white/60 font-body">
              {t("footer.designed")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
